interface Lead {
  id: string;
  lead_name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  priority: string;
  value: number | null;
  source: string | null;
  conversation_notes: string | null;
  next_action: string | null;
  next_action_date: string | null;
  created_at: string;
  custom_fields?: Record<string, any>;
  last_refreshed_at?: string | null;
}

interface ColumnConfig {
  id: string;
  name: string;
}

export const exportLeadsToCSV = (
  leads: Lead[],
  columnConfig: ColumnConfig[],
  filename: string,
  includeReminderDays: boolean = true,
  reminderDays: number = 30
) => {
  if (leads.length === 0) {
    return;
  }

  const calculateDaysRemaining = (lastRefreshedAt: string | null | undefined) => {
    if (!lastRefreshedAt) return reminderDays;
    const lastRefreshed = new Date(lastRefreshedAt);
    const now = new Date();
    const daysPassed = Math.floor((now.getTime() - lastRefreshed.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, reminderDays - daysPassed);
  };

  // Helper function to escape CSV cells
  const escapeCsvCell = (cell: any): string => {
    const cellStr = String(cell ?? '');
    // Always quote cells that contain commas, quotes, or newlines
    if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
      return `"${cellStr.replace(/"/g, '""')}"`;
    }
    return cellStr;
  };

  // Build headers - escape each header
  const headers = [
    'Name',
    'Company',
    'Email',
    'Phone',
    'Status',
    'Priority',
    ...columnConfig.map(col => escapeCsvCell(col.name)),
    'Next Action',
    ...(includeReminderDays ? ['Days Until Reminder'] : [])
  ];

  // Build rows
  const rows = leads.map(lead => {
    const baseRow = [
      escapeCsvCell(lead.lead_name),
      escapeCsvCell(lead.company),
      escapeCsvCell(lead.email),
      escapeCsvCell(lead.phone),
      escapeCsvCell(lead.status),
      escapeCsvCell(lead.priority),
      ...columnConfig.map(col => escapeCsvCell(lead.custom_fields?.[col.name])),
      escapeCsvCell(lead.next_action),
      ...(includeReminderDays ? [escapeCsvCell(calculateDaysRemaining(lead.last_refreshed_at))] : [])
    ];
    return baseRow;
  });

  // Convert to CSV format - headers and rows are already escaped
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
